// Symmetry filter. Reflects/rotates a region of the input across the canvas.

import type { LayerSpec } from '@/core/pipeline';

type SymmetryParams = {
  mode: 'mirror-h' | 'mirror-v' | 'mirror-both' | 'radial-4' | 'radial-6' | 'radial-8' | 'kaleido';
  source: 'left' | 'right' | 'top' | 'bottom';
};

export const symmetrySpec: LayerSpec = {
  kind: 'symmetry',
  category: 'filter',
  animated: false,
  label: 'Symmetry',
  defaults: { mode: 'mirror-h', source: 'left' } satisfies SymmetryParams,
  schema: {
    mode: {
      type: 'select',
      label: 'Mode',
      options: ['mirror-h', 'mirror-v', 'mirror-both', 'radial-4', 'radial-6', 'radial-8', 'kaleido'],
      default: 'mirror-h',
    },
    source: {
      type: 'select',
      label: 'Source side',
      options: ['left', 'right', 'top', 'bottom'],
      default: 'left',
    },
  },
  run: (input, params) => {
    if (!input) return new ImageData(1, 1);
    const p = params as SymmetryParams;
    const w = input.width, h = input.height;
    const src = input.data;
    const out = new ImageData(w, h);
    const dst = out.data;

    const sample = (x: number, y: number): [number, number, number, number] => {
      const sx = (x % w + w) % w;
      const sy = (y % h + h) % h;
      const i = (sy * w + sx) * 4;
      return [src[i] as number, src[i + 1] as number, src[i + 2] as number, src[i + 3] as number];
    };

    const cx = (w - 1) / 2;
    const cy = (h - 1) / 2;

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sx = x, sy = y;
        switch (p.mode) {
          case 'mirror-h':
            sx = p.source === 'right' ? (x >= cx ? x : w - 1 - x) : (x <= cx ? x : w - 1 - x);
            break;
          case 'mirror-v':
            sy = p.source === 'bottom' ? (y >= cy ? y : h - 1 - y) : (y <= cy ? y : h - 1 - y);
            break;
          case 'mirror-both':
            sx = x <= cx ? x : w - 1 - x;
            sy = y <= cy ? y : h - 1 - y;
            break;
          case 'radial-4':
          case 'radial-6':
          case 'radial-8':
          case 'kaleido': {
            const segments =
              p.mode === 'radial-4' ? 4 :
              p.mode === 'radial-6' ? 6 :
              p.mode === 'radial-8' ? 8 :
              12;
            const dx = x - cx, dy = y - cy;
            const r = Math.sqrt(dx * dx + dy * dy);
            const ang = Math.atan2(dy, dx);
            const seg = (Math.PI * 2) / segments;
            let folded = ((ang % seg) + seg) % seg;
            if (p.mode === 'kaleido') {
              const half = seg / 2;
              folded = folded > half ? seg - folded : folded;
            }
            sx = cx + r * Math.cos(folded);
            sy = cy + r * Math.sin(folded);
            break;
          }
        }
        const [r, g, b, a] = sample(Math.round(sx), Math.round(sy));
        const di = (y * w + x) * 4;
        dst[di] = r; dst[di + 1] = g; dst[di + 2] = b; dst[di + 3] = a;
      }
    }
    return out;
  },
};
